import { useState } from 'react';
import { EMPTY_FILTERS, type ClaimFilters, type ClaimRow } from '../types/claims.types';

interface ClaimsWorkspace {
  readonly selected: ClaimRow | null;
  readonly filterOpen: boolean;
  readonly filterDraft: ClaimFilters;
  select: (claim: ClaimRow | null) => void;
  toggleFilterPanel: () => void;
  closeFilterPanel: () => void;
  setFilterDraft: (filters: ClaimFilters) => void;
  clearFilterDraft: () => void;
}

/**
 * What the claims section has open: the selected claim, whether the filter panel is showing, and the
 * criteria typed into it but not yet searched.
 *
 * The draft lives here rather than inside the panel because the panel unmounts when it closes, and
 * US-014 AC-007 requires those criteria to survive that. Everything resets when `policyNumber` changes:
 * a detail panel left open would otherwise keep the previous policyholder's claim on screen beside
 * another policy's grid (US-013 AC-002).
 *
 * Only one drawer is open at a time. Both are pinned to the same edge, so opening one has to close the
 * other or they would cover each other.
 */
export function useClaimsWorkspace(policyNumber: string): ClaimsWorkspace {
  const [selected, setSelected] = useState<ClaimRow | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState<ClaimFilters>(EMPTY_FILTERS);
  const [shownFor, setShownFor] = useState(policyNumber);

  if (shownFor !== policyNumber) {
    setShownFor(policyNumber);
    setSelected(null);
    setFilterOpen(false);
    setFilterDraft(EMPTY_FILTERS);
  }

  return {
    selected,
    filterOpen,
    filterDraft,
    select: (claim): void => {
      setSelected(claim);
      if (claim !== null) {
        setFilterOpen(false);
      }
    },
    toggleFilterPanel: (): void => {
      setFilterOpen((current) => {
        if (!current) {
          setSelected(null);
        }

        return !current;
      });
    },
    closeFilterPanel: (): void => {
      setFilterOpen(false);
    },
    setFilterDraft,
    clearFilterDraft: (): void => {
      setFilterDraft(EMPTY_FILTERS);
    },
  };
}
